import React, { useRef, useState, useEffect, useCallback } from 'react'
import { composeRefs } from 'emailThreadViewer/TextFinder/utils/composeRefs'

interface TruncateProps extends React.HTMLAttributes<HTMLDivElement> {
  id?: string
  children?: string
  ellipsis?: string
  trimWhitespace?: boolean
  width?: number
}

interface DataMap {
  text: string
}

const Truncate: React.FC<TruncateProps> = ({
  id,
  children = '',
  ellipsis = '…',
  trimWhitespace = false,
  width = 0,
  ...spanProps
}) => {
  const [truncatedText, setTruncatedText] = useState<string>('')
  const [dataMap] = useState<DataMap>({ text: children })
  const targetRef = useRef<HTMLDivElement>(null)
  const originalTextRef = useRef<HTMLSpanElement>(null)
  const ellipsisRef = useRef<HTMLSpanElement>(null)

  const observer = useRef<ResizeObserver | null>(null)
  const rafRef = useRef<number | null>(null)

  const measureRef = (node: HTMLDivElement | null): void => {
    if (observer.current) {
      observer.current.disconnect()
      observer.current = null
    }

    if (!node) return
    observer.current = new ResizeObserver(() => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      rafRef.current = requestAnimationFrame(calculateTruncation)
    })
    observer.current.observe(node)
  }

  // children이 변경될 때마다 계산 다시 수행
  useEffect(() => {
    dataMap.text = children
    calculateTruncation()
  }, [children, width, ellipsis])

  // 텍스트 너비 측정
  const measureWidth = (text: string): number => {
    if (!targetRef.current) return 0

    const style = window.getComputedStyle(targetRef.current)
    const font = `${style.fontWeight} ${style.fontStyle} ${style.fontSize} ${style.fontFamily}`
    const canvas = document.createElement('canvas').getContext('2d')
    if (!canvas) return 0
    canvas.font = font

    return canvas.measureText(text).width
  }

  // 말줄임 계산
  const calculateTruncation = (): void => {
    const { text } = dataMap

    if (!targetRef.current || typeof text !== 'string') return

    const containerWidth =
      width || (targetRef.current.parentNode as HTMLElement)?.getBoundingClientRect().width || 0
    if (!containerWidth) return setTruncatedText(text)

    const textLine = text.replace(/\n/g, ' ')

    // 줄이 컨테이너 너비보다 작거나 같으면 자르지 않음
    if (measureWidth(textLine) <= containerWidth) {
      return setTruncatedText(textLine)
    }

    // 이진 탐색으로 적합한 길이 찾기
    let low = 0
    let high = textLine.length
    let lastValidText = ''

    while (low <= high) {
      const mid = Math.floor((low + high) / 2)
      const testText = textLine.substring(0, mid)

      if (measureWidth(testText + ellipsis) <= containerWidth) {
        lastValidText = testText
        low = mid + 1
      } else {
        high = mid - 1
      }
    }

    // 단어 단위로 자르기 (선택적)
    if (trimWhitespace && lastValidText.length > 0) {
      const lastSpaceIdx = lastValidText.lastIndexOf(' ')
      if (lastSpaceIdx !== -1) {
        lastValidText = lastValidText.substring(0, lastSpaceIdx)
      }
    }

    setTruncatedText(lastValidText + ellipsis)
  }

  return (
    <div {...spanProps} ref={useCallback(composeRefs(targetRef, measureRef), [])}>
      {truncatedText}
      <span ref={originalTextRef} style={{ display: 'none' }}>
        {children}
      </span>
      <span ref={ellipsisRef} style={{ position: 'absolute', visibility: 'hidden' }}>
        {ellipsis}
      </span>
    </div>
  )
}

export default Truncate
